/**
 * Session Deal — a per-visitor countdown discount for Shopify themes.
 *
 * The discount starts at `start`% the first time a visitor lands on the store,
 * drops by `step`% every `interval` seconds, and bottoms out at 0%. The clock is
 * stored in a session cookie, so it survives refreshes and navigation (and is
 * shared across tabs) but resets when the browser is fully closed.
 *
 * At checkout the script applies the discount code that matches the current
 * step via Shopify's /discount/<CODE>?redirect=/checkout URL.
 *
 * Config is read from the first element with a [data-session-deal] attribute
 * (JSON). See sections/session-deal.liquid.
 */
(function () {
  'use strict';

  // The script tag may sit above the bar's markup, so wait until the DOM is parsed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
  var root = document.querySelector('[data-session-deal]');
  if (!root || window.SessionDeal) return;

  var cfg;
  try {
    cfg = JSON.parse(root.getAttribute('data-session-deal'));
  } catch (e) {
    console.error('[SessionDeal] invalid config JSON', e);
    return;
  }

  var START = Number(cfg.start) || 49;
  var STEP = Number(cfg.step) || 5;
  var INTERVAL = Number(cfg.interval) || 10; // seconds
  var COOKIE = cfg.cookieName || 'sd_start';
  var CODES = parseCodes(cfg.codes);

  /* ---------- session clock (session cookie: no expiry => cleared on browser close) ---------- */

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  var startedAt = parseInt(getCookie(COOKIE), 10);
  if (!startedAt || isNaN(startedAt) || startedAt > Date.now()) {
    startedAt = Date.now();
    setCookie(COOKIE, String(startedAt));
  }

  /* ---------- ladder maths ---------- */

  function getState() {
    var elapsed = (Date.now() - startedAt) / 1000;
    var idx = Math.floor(elapsed / INTERVAL);
    var pct = Math.max(0, START - idx * STEP);
    var expired = pct === 0;
    var next = expired ? 0 : Math.max(0, START - (idx + 1) * STEP);
    var secondsLeft = expired ? 0 : INTERVAL - (elapsed % INTERVAL);
    var totalSteps = Math.ceil(START / STEP); // steps until 0
    return {
      pct: pct,
      next: next,
      secondsLeft: secondsLeft,
      expired: expired,
      elapsed: elapsed,
      progress: Math.min(1, elapsed / (totalSteps * INTERVAL)),
      code: CODES[pct] || null
    };
  }

  function parseCodes(raw) {
    var map = {};
    if (!raw) return map;
    if (typeof raw === 'object') {
      Object.keys(raw).forEach(function (k) { map[parseInt(k, 10)] = String(raw[k]).trim(); });
      return map;
    }
    String(raw).split(/\r?\n/).forEach(function (line) {
      var m = line.match(/^\s*(\d+)\s*[=:]\s*(\S+)\s*$/);
      if (m) map[parseInt(m[1], 10)] = m[2];
    });
    return map;
  }

  /* ---------- money formatting (mirrors Shopify's money filter) ---------- */

  function formatMoney(cents, format) {
    format = format || cfg.moneyFormat || '{{amount}}';
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) number = 0;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    switch ((format.match(placeholder) || [])[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* ---------- product price (product pages only) ---------- */

  function currentVariantPrice() {
    var p = cfg.product;
    if (!p) return null;
    var id = new URLSearchParams(location.search).get('variant');
    if (id && p.variants) {
      for (var i = 0; i < p.variants.length; i++) {
        if (String(p.variants[i].id) === String(id)) return p.variants[i].price;
      }
    }
    return p.price;
  }

  function discounted(cents, pct) {
    return Math.round(cents * (100 - pct) / 100);
  }

  /* ---------- rendering ---------- */

  var els = {
    pct: document.querySelectorAll('[data-sd-pct]'),
    next: document.querySelectorAll('[data-sd-next]'),
    seconds: document.querySelectorAll('[data-sd-seconds]'),
    bar: document.querySelectorAll('[data-sd-bar]'),
    priceNow: document.querySelectorAll('[data-sd-price-now]'),
    priceWas: document.querySelectorAll('[data-sd-price-was]'),
    live: document.querySelectorAll('[data-sd-live]'),
    expired: document.querySelectorAll('[data-sd-expired]'),
    wrap: document.querySelectorAll('[data-sd-wrap]')
  };

  function setText(list, text) {
    for (var i = 0; i < list.length; i++) if (list[i].textContent !== text) list[i].textContent = text;
  }

  var lastPct = null;

  function render() {
    var s = getState();
    setText(els.pct, s.pct + '%');
    setText(els.next, s.next + '%');
    setText(els.seconds, String(Math.ceil(s.secondsLeft)));
    for (var i = 0; i < els.bar.length; i++) els.bar[i].style.width = (100 - s.progress * 100) + '%';

    var base = currentVariantPrice();
    if (base != null) {
      setText(els.priceNow, formatMoney(discounted(base, s.pct)));
      setText(els.priceWas, formatMoney(base));
      if (cfg.rewritePrice !== false) rewriteThemePrice(s);
    }

    renderUpcart(s);
    for (i = 0; i < els.live.length; i++) els.live[i].hidden = s.expired;
    for (i = 0; i < els.expired.length; i++) els.expired[i].hidden = !s.expired;
    for (i = 0; i < els.wrap.length; i++) els.wrap[i].classList.toggle('sd--expired', s.expired);

    if (lastPct !== null && lastPct !== s.pct) {
      for (i = 0; i < els.wrap.length; i++) {
        els.wrap[i].classList.remove('sd--tick');
        void els.wrap[i].offsetWidth; // restart animation
        els.wrap[i].classList.add('sd--tick');
      }
      document.dispatchEvent(new CustomEvent('sessiondeal:step', { detail: s }));
      syncCart();
    }
    lastPct = s.pct;
  }

  // Rewrite the theme's own product price so it shows the live discounted price.
  // Uses cfg.priceSelector if set; otherwise finds the element(s) on the page whose
  // text is exactly one of the product's variant prices (so it survives variant swaps
  // and theme re-renders).
  function findThemePriceElements() {
    if (cfg.priceSelector) {
      return Array.prototype.slice.call(document.querySelectorAll(cfg.priceSelector)).map(function (el) {
        return { el: el, base: currentVariantPrice() };
      });
    }
    var p = cfg.product;
    if (!p) return [];
    var prices = {};
    var list = (p.variants && p.variants.length ? p.variants : [{ price: p.price }]);
    list.forEach(function (v) { prices[formatMoney(v.price).trim()] = v.price; });
    var known = Object.keys(prices);
    var scope = document.querySelector('main, #MainContent, .main-content, [role="main"]') || document.body;
    var found = [];
    var all = scope.querySelectorAll('span, div, p, dd, strong, b, bdi');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest('[data-session-deal], #CartDrawer, .cart-drawer, [id^="cart"], [id^="Cart"]')) continue;
      var isOurs = el.hasAttribute('data-sd-theme-price');
      if (isOurs && !el.querySelector('.sd-theme-now') && el.innerHTML !== el.getAttribute('data-sd-original')) {
        // the theme overwrote our markup (variant change) — treat as a fresh element
        el.removeAttribute('data-sd-theme-price'); el.removeAttribute('data-sd-original'); el.removeAttribute('data-sd-original-text');
        isOurs = false;
      }
      if (!isOurs && el.children.length) continue; // only leaf elements hold the price text
      if (!isOurs && el.closest('[data-sd-theme-price]')) continue;
      var text = (isOurs ? el.getAttribute('data-sd-original-text') : el.textContent).replace(/\s+/g, ' ').trim();
      for (var k = 0; k < known.length; k++) {
        if (text === known[k] || text.indexOf(known[k] + ' ') === 0) {
          found.push({ el: el, base: prices[known[k]] });
          break;
        }
      }
      if (found.length >= 1) break; // the first match in the page is the price under the title; leave product cards alone
    }
    return found;
  }

  function rewriteThemePrice(s) {
    var pct = s.pct;
    var targets = findThemePriceElements();
    var labels = cfg.labels || {};
    for (var i = 0; i < targets.length; i++) {
      var t = targets[i].el, base = targets[i].base;
      if (base == null) continue;
      if (!t.hasAttribute('data-sd-theme-price')) {
        t.setAttribute('data-sd-theme-price', '');
        t.setAttribute('data-sd-original', t.innerHTML);
        t.setAttribute('data-sd-original-text', t.textContent.replace(/\s+/g, ' ').trim());
      }
      var html;
      if (pct > 0) {
        html = '<s class="sd-theme-was">' + formatMoney(base) + '</s> <span class="sd-theme-now">' + formatMoney(discounted(base, pct)) + '</span>';
        if (cfg.inlineTimer !== false) {
          html += ' <span class="sd-inline" aria-live="off">' +
            (labels.dropsTo || 'drops to') + ' <b>' + s.next + '%</b> ' +
            (labels.in || 'in') + ' <b class="sd-inline__s">' + Math.ceil(s.secondsLeft) + 's</b></span>';
        }
      } else {
        html = t.getAttribute('data-sd-original');
      }
      if (t.innerHTML !== html) t.innerHTML = html;
    }
  }

  /* ---------- cart: keep the current step's code applied to the cart ---------- */

  // Shopify's Ajax Cart API accepts a `discount` field on /cart/update.js, so the
  // cart drawer, cart page and checkout all reflect the live discount.
  var SECTION_TARGETS = {
    'cart-drawer': '#CartDrawer',
    'cart-icon-bubble': '#cart-icon-bubble',
    'main-cart-items': '#main-cart-items',
    'main-cart-footer': '#main-cart-footer'
  };
  var lastSyncedCode = null, syncing = false, syncQueued = false;

  function syncCart(force) {
    if (cfg.syncCart === false) return;
    var code = getState().code || '';
    if (!force && code === lastSyncedCode) return;
    if (syncing) { syncQueued = true; return; }
    syncing = true;
    lastSyncedCode = code;

    var sectionIds = Object.keys(SECTION_TARGETS).filter(function (id) {
      return document.querySelector(SECTION_TARGETS[id]);
    });

    function post(withSections) {
      var body = { discount: code };
      if (withSections && sectionIds.length) body.sections = sectionIds.join(',');
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok && withSections) return post(false); // theme lacks one of the sections
        return r.json();
      });
    }

    post(true).then(function (cart) {
      if (cart && typeof cart.items_subtotal_price === 'number') lastCart = cart;
      if (cart && cart.sections) swapSections(cart.sections);
      document.dispatchEvent(new CustomEvent('sessiondeal:cartsynced', { detail: { code: code, cart: cart } }));
    }).catch(function (e) {
      console.warn('[SessionDeal] cart sync failed', e);
    }).then(function () {
      syncing = false;
      if (syncQueued) { syncQueued = false; syncCart(true); }
    });
  }

  function swapSections(sections) {
    Object.keys(sections).forEach(function (id) {
      var sel = SECTION_TARGETS[id];
      var target = sel && document.querySelector(sel);
      var html = sections[id];
      if (!target || !html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector(sel);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  }

  // Re-sync after the theme adds/changes items so the discount is on the cart before it renders.
  (function watchCartRequests() {
    var re = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var isOurs = function (body) { return typeof body === 'string' && body.indexOf('"discount"') !== -1; };
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (re.test(url) && !(init && isOurs(init.body))) {
        p.then(function () { setTimeout(function () { syncCart(true); }, 50); }, function () {});
      }
      return p;
    };
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (re.test(String(url))) {
        this.addEventListener('loadend', function () { setTimeout(function () { syncCart(true); }, 50); });
      }
      return origOpen.apply(this, arguments);
    };
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && /\/cart\/add/.test(f.getAttribute('action') || '')) setTimeout(function () { syncCart(true); }, 800);
    }, true);
  })();

  /* ---------- UpCart drawer: show the deal inside the drawer ---------- */

  // UpCart renders in a shadow root and shows the full subtotal unless its own discount
  // feature is on. We draw a deal line above its checkout button and put the discounted
  // total in the button label, so the drawer always agrees with the bar and checkout.
  var lastCart = null;

  function upcartRoot() {
    return window.upcartDocumentOrShadowRoot || null;
  }

  var MONEY_RE = /(?:[£$€¥]\s?\d[\d.,]*|\d[\d.,]*\s?(?:[£$€¥]|[A-Z]{3}))/; // $85.98, €85,98, 85.98 EUR
  var origText = typeof WeakMap === 'function' ? new WeakMap() : null;

  function renderUpcart(s) {
    var root = upcartRoot();
    if (!root || cfg.upcart === false) return;
    var btn = root.querySelector('.upcart-checkout-button');
    if (!btn) return;
    var labels = cfg.labels || {};
    var colors = cfg.colors || {};
    var accent = colors.accent || '#ffd400';
    var subtotal = lastCart && typeof lastCart.items_subtotal_price === 'number' ? lastCart.items_subtotal_price : null;

    // 1) deal line just above the checkout button
    var line = root.querySelector('.sd-upcart-line');
    if (!line) {
      line = document.createElement('div');
      line.className = 'sd-upcart-line';
      line.setAttribute('style',
        'display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 0 10px;padding:10px 14px;border-radius:10px;' +
        'background:' + (colors.bg || '#111') + ';color:' + (colors.fg || '#fff') + ';font-size:14px;line-height:1.3;');
      btn.parentNode.insertBefore(line, btn);
    }
    var html;
    if (s.expired) {
      html = '<span>' + (labels.expired || 'Your deal has run out') + '</span>';
    } else {
      html = '<span><strong style="color:' + accent + ';font-size:1.15em">' + s.pct + '% ' + (labels.offShort || 'off') + '</strong>' +
        ' <span style="opacity:.85">· ' + (labels.dropsTo || 'drops to') + ' <b>' + s.next + '%</b> ' +
        (labels.in || 'in') + ' <b style="color:' + accent + '">' + Math.ceil(s.secondsLeft) + 's</b></span></span>' +
        (subtotal != null ? '<strong style="color:' + accent + '">−' + formatMoney(subtotal - discounted(subtotal, s.pct)) + '</strong>' : '');
    }
    if (line.innerHTML !== html) line.innerHTML = html;

    // 2) discounted total in the checkout button label (swap only the money text node)
    if (subtotal == null || !origText) return;
    var walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      var orig = origText.get(node);
      if (orig == null) {
        if (!MONEY_RE.test(node.nodeValue)) continue;
        orig = node.nodeValue; origText.set(node, orig);
      }
      var target = s.expired ? orig : orig.replace(MONEY_RE, formatMoney(discounted(subtotal, s.pct)));
      if (node.nodeValue !== target) node.nodeValue = target;
      break;
    }
  }

  // Keep our cart snapshot fresh from UpCart's own load events too.
  (function hookUpcartEvents() {
    function onLoaded(cart) { if (cart && typeof cart.items_subtotal_price === 'number') lastCart = cart; }
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      try { window.upcartSubscribeCartLoaded(onLoaded); } catch (e) {}
    } else {
      var prev = window.upcartOnCartLoaded;
      window.upcartOnCartLoaded = function (cart) { onLoaded(cart); if (typeof prev === 'function') prev(cart); };
    }
  })();

  // Console helper: SessionDeal.diagnose() — what does Shopify's cart say right now?
  function diagnose() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
      var s = getState();
      var codes = cart.discount_codes || [];
      console.log('[SessionDeal] step:', s.pct + '%', 'code:', s.code);
      console.log('[SessionDeal] cart discount_codes:', JSON.stringify(codes));
      console.log('[SessionDeal] items_subtotal_price:', cart.items_subtotal_price, 'total_discount:', cart.total_discount, 'total_price:', cart.total_price);
      if (!codes.length) console.warn('[SessionDeal] No code on the cart. Cart sync may be blocked — check the Network tab for /cart/update.js.');
      else if (codes[0].applicable === false) console.warn('[SessionDeal] Code "' + codes[0].code + '" is on the cart but NOT applicable: check in Discounts that it is active, has no minimum, applies to these products, and that no other discount blocks it.');
      else console.log('[SessionDeal] Code applied. Checkout will show it.');
      return cart;
    });
  }

  /* ---------- checkout: apply the code for the current step ---------- */

  function checkoutUrl() {
    var s = getState();
    if (!s.code) return '/checkout';
    return '/discount/' + encodeURIComponent(s.code) + '?redirect=' + encodeURIComponent('/checkout');
  }

  function goToCheckout() {
    window.location.href = checkoutUrl();
  }

  if (cfg.hookCheckout !== false) {
    var SEL = 'button[name="checkout"], input[name="checkout"], a[href^="/checkout"], [data-sd-checkout]' +
      ', .upcart-checkout-button' +                       // UpCart (renders inside a shadow root)
      (cfg.checkoutSelectors ? ', ' + cfg.checkoutSelectors : '');

    function findCheckoutTarget(e) {
      // composedPath() sees through shadow DOM (cart drawer apps like UpCart); fall back to closest().
      var path = e.composedPath ? e.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (n && n.nodeType === 1 && n.matches) {
          try { if (n.matches(SEL)) return n; } catch (err) { /* bad selector in setting */ }
        }
      }
      return e.target && e.target.closest ? e.target.closest(SEL) : null;
    }

    document.addEventListener('click', function (e) {
      var t = findCheckoutTarget(e);
      if (!t) return;
      if (t.classList && (t.classList.contains('upcartDisableCheckoutButton') || t.hasAttribute('disabled'))) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      goToCheckout();
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && /\/checkout(\?|$)/.test(f.getAttribute('action') || '')) {
        e.preventDefault();
        goToCheckout();
      }
    }, true);
  }

  /* ---------- keep prices in sync when the theme swaps variants (Dawn uses replaceState) ---------- */

  ['pushState', 'replaceState'].forEach(function (fn) {
    var orig = history[fn];
    history[fn] = function () {
      var r = orig.apply(this, arguments);
      setTimeout(render, 0);
      return r;
    };
  });
  window.addEventListener('popstate', render);
  document.addEventListener('variant:change', render);

  /* ---------- keep the page offset equal to the bar's real height (fixed bar only) ---------- */

  (function trackBarHeight() {
    var bar = root.closest('[data-sd-wrap]') || root;
    function apply() {
      if (getComputedStyle(bar).position !== 'fixed') return;
      var h = Math.ceil(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--sd-bar-h', h + 'px');
    }
    apply();
    if (window.ResizeObserver) new ResizeObserver(apply).observe(bar);
    window.addEventListener('resize', apply);
    window.addEventListener('load', apply);
  })();

  /* ---------- go ---------- */

  render();
  syncCart(true);
  var timer = setInterval(render, 250);

  window.SessionDeal = {
    getState: getState,
    checkoutUrl: checkoutUrl,
    syncCart: function () { syncCart(true); },
    diagnose: diagnose,
    goToCheckout: goToCheckout,
    formatMoney: formatMoney,
    reset: function () { // handy while testing: SessionDeal.reset()
      startedAt = Date.now();
      setCookie(COOKIE, String(startedAt));
      lastPct = null;
      render();
    },
    stop: function () { clearInterval(timer); }
  };
  } // init
})();